import { CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import * as moment from 'moment';
import { sort } from 'remeda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { LmoFracListSummary, VendorDetail } from '~lmo/models/frac.model';
import { loadPluralMapping } from '~lmo/pluralMappings';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { overflowTruckingVendors } from '../../../pluralMappings';

const LMO_FRAC_LIST_SORTING_ORDER_KEY = `SANDi LMO FRAC LIST SORTING ORDER`;

interface LmoFracListSummaryStageWarningBool extends LmoFracListSummary {
  stageWarning: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-list',
  styleUrls: ['./lmo-frac-list.component.scss'],
  templateUrl: './lmo-frac-list.component.html',
})
export class LmoFracListComponent implements OnInit, AfterViewInit {
  private fracSortingOrder$$ = new BehaviorSubject<number[]>([]);
  public overflowTruckingVendors = overflowTruckingVendors;
  public loadPluralMapping = loadPluralMapping;
  public fracs$: Observable<LmoFracListSummaryStageWarningBool[]>;
  public isLoading$ = this.fracService.fracSummaryListLoading$;
  public statsLoading$ = this.fracService.fracVendorAllocationStatsLoading$;

  @ViewChildren(CdkDropList) private dropsQuery: QueryList<CdkDropList>;

  public drops: CdkDropList[];
  public fracArray: LmoFracListSummaryStageWarningBool[];

  constructor(private fracService: LmoFracsService) {
    const savedSortingOrder = localStorage.getItem(LMO_FRAC_LIST_SORTING_ORDER_KEY);
    if (savedSortingOrder) {
      this.fracSortingOrder$$.next(JSON.parse(savedSortingOrder));
    }
  }

  public ngOnInit() {
    this.fracs$ = combineLatest(
      this.fracSortingOrder$$.pipe(
        distinctUntilChanged(),
        map((fracOrder) =>
          fracOrder.reduce((acc, fracId, index) => {
            acc[fracId] = index;
            return acc;
          }, {} as Record<number, number>),
        ),
      ),
      this.fracService.fracSummaries$.pipe(map((fracs) => fracs.map(summaryToStageWarningSummary))),
    ).pipe(
      map(([sortingOrder, fracs]) => {
        this.fracArray = sort(fracs, sortFrac(sortingOrder));
        return sort(fracs, sortFrac(sortingOrder));
      }),
    );
  }

  public ngAfterViewInit() {
    this.dropsQuery.changes.subscribe(() => {
      this.drops = this.dropsQuery.toArray();
    });
    Promise.resolve().then(() => {
      this.drops = this.dropsQuery.toArray();
    });
  }

  public drop(event: CdkDragDrop<any>) {
    moveItemInArray(this.fracArray, event.previousContainer.data.index, event.container.data.index);
    this.setNewSortOrder();
  }

  public trackById(_index: number, summary: LmoFracListSummaryStageWarningBool): number {
    return summary.lmoFracSummary.id;
  }

  public trackByFctvId(_index: number, detail: VendorDetail): string {
    return detail.fctvId;
  }

  public clearCustomSort() {
    this.fracSortingOrder$$.next([]);
    localStorage.removeItem(LMO_FRAC_LIST_SORTING_ORDER_KEY);
  }

  private setNewSortOrder() {
    const currentSortOrder = this.fracArray.map((summary) => summary.lmoFracSummary.id);
    localStorage.setItem(LMO_FRAC_LIST_SORTING_ORDER_KEY, JSON.stringify(currentSortOrder));
  }
}

function summaryToStageWarningSummary(summary: LmoFracListSummary): LmoFracListSummaryStageWarningBool {
  if (!summary.lmoFracSummary.previousStageCompletedTime) {
    return {
      ...summary,
      stageWarning: false,
    };
  }
  const mostRecentStageCompletedTime = moment(summary.lmoFracSummary.previousStageCompletedTime);
  const expectedCurrentCompletionTimeWithBuffer = mostRecentStageCompletedTime
    .clone()
    .add(summary.lmoFracSummary.previousStageDownTime)
    .add(summary.lmoFracSummary.currentStagePumpTime, 'minutes')
    .add(2, 'hours');
  if (moment().isAfter(expectedCurrentCompletionTimeWithBuffer)) {
    return {
      ...summary,
      stageWarning: true,
    };
  }
  return {
    ...summary,
    stageWarning: false,
  };
}

function sortFrac(
  sortingOrder: Record<number, number>,
): (a: LmoFracListSummaryStageWarningBool, b: LmoFracListSummaryStageWarningBool) => number {
  return (a: LmoFracListSummaryStageWarningBool, b: LmoFracListSummaryStageWarningBool): number => {
    const aSortingOrder = sortingOrder[a.lmoFracSummary.id];
    const bSortingOrder = sortingOrder[b.lmoFracSummary.id];
    if (aSortingOrder && bSortingOrder) {
      return aSortingOrder - bSortingOrder;
    } else if (aSortingOrder) {
      return 1;
    } else if (bSortingOrder) {
      return -1;
    } else {
      return a.lmoFracSummary.name.toLocaleLowerCase().localeCompare(b.lmoFracSummary.name.toLocaleLowerCase());
    }
  };
}
